/**
 * Phase N — /about full rewrite.
 *
 * Positioning: Beyond Valencia is the national platform for transfer strategy,
 * educational mobility, and second-chance admissions. Copy avoids fabricated
 * numeric claims (see backend/tests/test_positioning.py for the enforced
 * substring blocklist). Softening language and defensible facts only.
 *
 * TODO: swap MonogramMark for founder headshot when provided.
 * TODO: confirm exact legal entity name.
 */
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";
import MonogramMark from "@/components/MonogramMark";
import { faqJsonLd, ABOUT_FAQ } from "@/lib/faqSchemas";

const PERSON_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Taimur Ahmed",
  jobTitle: "Founder, Beyond Valencia",
  alumniOf: [
    { "@type": "CollegeOrUniversity", name: "Harvard University" },
    { "@type": "CollegeOrUniversity", name: "University of Florida" },
    { "@type": "CollegeOrUniversity", name: "Valencia College" },
  ],
  worksFor: { "@type": "Organization", name: "Beyond Valencia" },
};

const CREDENTIALS = [
  "Harvard ALM '17 — History & Government",
  "Founder, Ivy League Consulting (2018–present)",
  "Crimson Courage — Harvard Alumni for Academic Freedom",
  "Phillips Brooks House Association",
  "Harvard Club of Central Florida (Admissions)",
  "Former Harvard Admissions Interviewer",
  "Public Historian & Civic Storyteller",
  "12 schools in 12 years · Valencia → UF → Harvard",
];

function Eyebrow({ children, tone = "brass" }) {
  return (
    <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${tone === "brass" ? "text-brass" : "text-brass-soft"}`}>
      {children}
    </div>
  );
}

function Section({ eyebrow, title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="border-b border-stone/20 py-14 sm:py-16">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-6 max-w-3xl text-ink-soft leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

export default function About() {
  return (
    <Layout>
      <SEO
        title="About | Beyond Valencia"
        description="The national platform for transfer strategy, educational mobility, and second-chance admissions. Founded by Taimur Ahmed, Harvard ALM '17 — a former community college transfer student."
        canonical="/about"
        ogType="profile"
        jsonLd={[PERSON_JSONLD, faqJsonLd(ABOUT_FAQ)]}
      />
      <LegalReviewBanner />

      {/* Hero */}
      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="about-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16 lg:pb-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Eyebrow tone="soft">About Beyond Valencia</Eyebrow>
            <h1
              className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.5rem] text-bone leading-[1.05] tracking-tight mt-5"
              data-testid="about-h1"
            >
              The national platform for transfer strategy, educational mobility, and second-chance admissions.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-bone-dim leading-relaxed max-w-2xl">
              Beyond Valencia was founded on a single conviction: where you begin should never determine where you finish.
              We are a multidisciplinary team of educational strategists, transfer specialists, and technology partners
              building the transfer intelligence layer American higher education has been missing.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            {/* TODO: swap for real founder headshot when provided */}
            <MonogramMark initials="TA" size="lg" subtitle="Founder & CEO" />
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Section eyebrow="Our mission" title="Educational mobility, honestly delivered." id="mission" testId="about-mission">
          <p>
            Beyond Valencia exists to make the transfer pathway visible, reachable, and repeatable. We build verified
            requirement graphs, publish defensible diagnostics, and pair students with human strategists who understand
            what admissions offices are actually reading.
          </p>
          <p>
            Every prerequisite, GPA threshold, and articulation claim on our platform maps to a verified source URL.
            We do not fabricate admissions data. We do not invent course codes. We do not sell hope.
          </p>
        </Section>

        <Section eyebrow="What we are" title="A transfer intelligence platform — not an application service." id="what-we-are" testId="about-what-we-are">
          <p>
            Beyond Valencia is a national education company built around three products: a verified transfer
            requirement graph, a diagnostic engine that surfaces where a student actually stands, and a strategy
            layer delivered by human advisers backed by that same data.
          </p>
          <p>
            We do not complete transfer applications. We engineer transfer success — sequencing decisions from a
            student&apos;s first semester at community college to their acceptance letter at a four-year university.
          </p>
        </Section>

        <Section eyebrow="Who we serve" title="Ambitious students on non-traditional paths." id="who" testId="about-who">
          <p>
            Community college students, non-traditional applicants, gap-year returners, and students recovering from
            a rejected freshman cycle — a growing community of learners whose starting point was underestimated by
            the traditional admissions industry.
          </p>
          <p>
            We are Florida-anchored (Valencia, Seminole, Santa Fe, Daytona, Broward, Miami Dade, and every other
            community college in the state) with an emerging national footprint through our partnership network.
          </p>
        </Section>

        <Section eyebrow="Founder" title="Taimur Ahmed — Harvard ALM '17." id="founder" testId="about-founder">
          <p>
            Beyond Valencia was founded by Taimur Ahmed, a former community college transfer student. Twelve
            different schools in twelve years. Started at Valencia College. Transferred to the University of
            Florida. Earned his Master of Liberal Arts in History and Government from Harvard University in 2017.
          </p>
          <p>
            Through the Harvard Club of Central Florida he has supported alumni admissions education and interviewer
            training. He is an active voice in Crimson Courage, the Harvard Alumni for Academic Freedom initiative,
            and served the Phillips Brooks House Association during his Harvard years.
          </p>
          <p>
            He founded Ivy League Consulting in 2018 to provide historically grounded, equity-driven academic and
            civic education. Beyond Valencia is his Florida-anchored extension of that work — built specifically
            for the transfer pathway.
          </p>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 list-none" data-testid="about-credentials-list">
            {CREDENTIALS.map((c) => (
              <li key={c} className="flex gap-2.5 text-sm text-ink leading-relaxed">
                <span className="text-brass mt-2.5 h-px w-3 bg-brass flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section eyebrow="Team" title="A multidisciplinary team." id="team-blurb" testId="about-team-blurb">
          <p>
            Beyond Valencia is delivered by a multidisciplinary team of transfer strategists, admissions researchers,
            and technology partners. Additional adviser profiles will be published as new team members join.
          </p>
          <p>
            <Link to="/team" className="text-ivy hover:text-brass underline font-medium" data-testid="about-team-link">
              Meet the team →
            </Link>
          </p>
        </Section>

        <Section eyebrow="Partnership" title="Two teams. Honest split." id="partnership" testId="about-partnership">
          <p>
            Beyond Valencia is the Florida transfer engine — verified requirement graphs, the Mishap Corrector
            diagnostic, and the paid Florida Transfer tier. Ivy League Consulting is our partner for elite and Ivy
            admissions strategy: transfer targets like Harvard, Yale, Princeton, Cornell, Stanford, and top publics
            outside Florida.
          </p>
          <p>
            When a student&apos;s target list includes any Ivy, elite, or out-of-state destination, we route the
            intake to Ivy League Consulting — additive, not exclusive. Beyond Valencia still delivers a Florida-side
            plan when the data supports it.
          </p>
          <p>
            <Link
              to="/partners/ivy-league-consulting"
              className="inline-block mt-2 border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6"
              data-testid="about-partnership-cta"
            >
              Read about the partnership →
            </Link>
          </p>
        </Section>

        <Section eyebrow="What we owe you" title="The promise on every claim." id="promise" testId="about-promise">
          <p>
            Every claim on this platform is defensible. Every source URL is real and reachable. Every AI-generated
            recommendation is grounded in verified data or clearly labeled as illustrative. Every recommendation to
            seek help outside our platform — including our own partner — is made honestly because it serves the
            student, not our funnel.
          </p>
          <p>
            You can read the specifics on the{" "}
            <Link to="/methodology" className="text-ivy hover:text-brass underline" data-testid="about-methodology-link">
              methodology page
            </Link>
            {" "}and see the current verification counts live.
          </p>
        </Section>

        <Section eyebrow="Contact" title="Talk to us." id="contact" testId="about-contact">
          <p>
            {/* TODO: confirm mailbox routing for hello@ before publishing */}
            General inquiries:{" "}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="about-hello-email">
              hello@beyondvalencia.com
            </a>
          </p>
          <p>
            {/* TODO: confirm mailbox routing for press@ before publishing */}
            Press &amp; media:{" "}
            <a href="mailto:press@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="about-press-email">
              press@beyondvalencia.com
            </a>
          </p>
          {/* TODO: confirm exact legal entity name; TODO: no phone until founder provides one */}
          <p className="text-sm text-stone">Beyond Valencia LLC · Orlando, Florida</p>
        </Section>
      </div>
    </Layout>
  );
}
